import { useCallback, useMemo } from 'react'

/**
 * Returns props to spread onto an element to isolate it from React Flow's
 * event handling (drag, pan, click propagation).
 *
 * Use this hook when a wrapper component (`NodeInteractionZone`) is not
 * ergonomic — e.g. the element needs its own ref or specific event merging.
 *
 * For most cases, prefer `<NodeInteractionZone>` instead.
 */

interface NodeEventIsolationOptions {
  nopan?: boolean
}

interface NodeEventIsolationProps {
  className: string
  onClick: (e: React.SyntheticEvent) => void
  onKeyDown: (e: React.SyntheticEvent) => void
  onMouseDown: (e: React.SyntheticEvent) => void
}

export function useNodeEventIsolation(
  options: NodeEventIsolationOptions = {}
): NodeEventIsolationProps {
  const { nopan = false } = options

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation()
  }, [])

  const className = useMemo(() => {
    const classes = ['nodrag']
    if (nopan) classes.push('nopan')
    return classes.join(' ')
  }, [nopan])

  return useMemo(
    () => ({
      className,
      onClick: stopPropagation,
      onKeyDown: stopPropagation,
      onMouseDown: stopPropagation,
    }),
    [className, stopPropagation]
  )
}
